/* eslint-disable react/prop-types */
/**
 * PortalDocUploader — inline (Trust Contract: NO modal) widget that lets
 * a portal client drop their own POs / proof-of-payments. Iter 235.
 *
 * Mirrors the same multi-file + progress + per-file row UX as
 * `SalesDocEditor` but scoped to the two client categories.
 *
 * Hits `POST /api/portal/sales-documents/upload`. The server forces
 * customer_id to the caller's own customer, audience to "customer",
 * and category to one of `purchase_order` / `payment_proof`.
 */
import { useState } from "react";
import {
  Upload as UploadIcon,
  Loader2,
  CheckCircle2,
  Circle,
  XCircle,
  Send,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import DictTextarea from "../DictTextarea";

const CATEGORIES = [
  {
    key: "purchase_order",
    label: "Purchase order",
    desc: "Your PO referencing one of our quotes.",
  },
  {
    key: "payment_proof",
    label: "Proof of payment",
    desc: "Receipt or screenshot confirming you've paid an invoice.",
  },
];

function FileStatusIcon({ status }) {
  if (status === "uploaded") {
    return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />;
  }
  if (status === "uploading") {
    return <Loader2 className="w-3.5 h-3.5 text-cyan-300 shrink-0 animate-spin" />;
  }
  if (status === "failed") {
    return <XCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />;
  }
  return <Circle className="w-3.5 h-3.5 text-[#647087] shrink-0" />;
}

export default function PortalDocUploader({ onUploaded }) {
  const [category, setCategory] = useState("purchase_order");
  const [files, setFiles] = useState([]);
  const [fileStatus, setFileStatus] = useState([]);
  const [uploadPct, setUploadPct] = useState(0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const pickFiles = (e) => {
    const arr = Array.from(e.target.files || []);
    setFiles(arr);
    setFileStatus(arr.map((f) => ({
      name: f.name, size: f.size, status: "pending",
    })));
    setUploadPct(0);
    setErr("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (files.length === 0) {
      setErr("Pick at least one file to send.");
      return;
    }
    setBusy(true);
    setErr("");
    setFileStatus(files.map((f) => ({
      name: f.name, size: f.size, status: "uploading",
    })));
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("category", category);
      if (notes.trim()) fd.append("notes", notes.trim());
      files.forEach((f) => fd.append("files", f, f.name));
      const { data } = await api.post("/portal/sales-documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600_000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setUploadPct(Math.min(99, Math.round((evt.loaded / evt.total) * 100)));
        },
      });
      setUploadPct(100);
      const createdNames = new Set((data?.created || []).map((d) => d.file_name));
      setFileStatus((prev) => prev.map((row) => ({
        ...row,
        status: createdNames.has(row.name) ? "uploaded" : "failed",
        error: createdNames.has(row.name) ? null : "Upload skipped.",
      })));
      const n = data?.count || 0;
      toast.success(
        n === 1
          ? "1 document sent — we'll review it shortly."
          : `${n} documents sent — we'll review them shortly.`,
      );
      // Reset the picker (keep the per-file rows visible so the client
      // can read the receipts; they fade once more files are picked).
      setFiles([]);
      setNotes("");
      onUploaded?.(data?.created || []);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Upload failed.");
      setFileStatus((prev) => prev.map((row) => (
        row.status === "uploading" ? { ...row, status: "failed", error: "Failed." } : row
      )));
    } finally {
      setBusy(false);
    }
  };

  const activeCat = CATEGORIES.find((c) => c.key === category) || CATEGORIES[0];

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-3"
      data-testid="portal-doc-uploader"
    >
      <header className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#A0AABF] font-bold">
        <UploadIcon className="w-3.5 h-3.5" />
        Send a document
      </header>

      {/* Category tiles */}
      <div className="grid grid-cols-2 gap-2" data-testid="portal-doc-uploader-categories">
        {CATEGORIES.map((c) => {
          const selected = category === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategory(c.key)}
              data-testid={`portal-doc-cat-${c.key}`}
              className={`text-left rounded-md border p-2.5 transition ${
                selected
                  ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-100"
                  : "border-white/10 bg-transparent text-[#cbd5e1] hover:border-white/25"
              }`}
            >
              <div className="text-xs font-semibold">{c.label}</div>
              <div className="text-[10px] text-[#647087] mt-0.5">{c.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
            Files (drop one or many)
          </span>
          <input
            type="file"
            multiple
            onChange={pickFiles}
            className="block mt-1 text-xs text-[#a0aabf]"
            data-testid="portal-doc-uploader-file-input"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
            Note (optional)
          </span>
          <DictTextarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={`Anything we should know about this ${activeCat.label.toLowerCase()}?`}
            rows={2}
            className="hv-input mt-1 text-xs"
            data-testid="portal-doc-uploader-notes"
          />
        </label>
      </div>

      {/* Progress + per-file rows */}
      {(files.length > 0 || fileStatus.length > 0) && (
        <div className="space-y-2" data-testid="portal-doc-uploader-progress">
          {(busy || uploadPct > 0) && (
            <div className="space-y-1">
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full bg-cyan-400 transition-all duration-150"
                  style={{ width: `${uploadPct}%` }}
                />
              </div>
              <p className="text-[10px] text-[#647087]">
                {uploadPct < 100 ? `Sending… ${uploadPct}%` : "Sent — we're reading them now."}
              </p>
            </div>
          )}
          <ul className="max-h-40 overflow-y-auto space-y-1">
            {fileStatus.map((row, i) => (
              <li
                key={i}
                className="flex items-center gap-2 text-[10px]"
                data-testid={`portal-doc-uploader-row-${i}`}
              >
                <FileStatusIcon status={row.status} />
                <span className="truncate flex-1 text-[#a0aabf]">{row.name}</span>
                <span className="text-[#647087] tabular-nums">
                  {Math.round(row.size / 1024)} KB
                </span>
                <span
                  className={
                    row.status === "uploaded" ? "text-emerald-300"
                      : row.status === "uploading" ? "text-cyan-300"
                      : row.status === "failed" ? "text-rose-300"
                      : "text-[#647087]"
                  }
                >
                  {row.status === "uploaded" ? "Received · reading"
                    : row.status === "uploading" ? "Sending"
                    : row.status === "failed" ? (row.error || "Failed")
                    : "Pending"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {err && (
        <div className="text-[11px] text-rose-300" data-testid="portal-doc-uploader-error">
          {err}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy || files.length === 0}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="portal-doc-uploader-submit"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {busy ? "Sending…" : files.length > 1 ? `Send ${files.length} documents` : "Send document"}
        </button>
      </div>
    </form>
  );
}
